import React, { useEffect, useState } from 'react';
import useLead from 'src/hooks/useLead';
import { useTranslation } from 'react-i18next';
import { Edit, ExternalLink } from 'react-feather';
import { IDuplicated, IDuplicateLeadMatch } from './types';
import { getDuplicateLeadMatch } from '../api/getDuplicateLeadMatch';
import {
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Divider,
    Button,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import {
    DUPLICATED_LEAD_AUTH,
    UPDATE_PERMISSION,
} from 'src/constants/permissions';
import useAuth from 'src/hooks/useAuth';

export const Duplicated = ({
    leadId,
    duplicatedURL,
    isDuplicated = false,
    ...props
}: IDuplicated) => {
    const { setAsDuplicated } = useLead();
    const { t } = useTranslation();
    const initiallyMarkedAsDuplicated =
        isDuplicated || Boolean(duplicatedURL);
    const [duplicateMatch, setDuplicateMatch] =
        useState<IDuplicateLeadMatch | null>(null);
    const [isLoadingMatch, setIsLoadingMatch] = useState(
        !initiallyMarkedAsDuplicated
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isMarkedAsDuplicated, setIsMarkedAsDuplicated] = useState(
        initiallyMarkedAsDuplicated
    );
    const [originalLeadURL, setOriginalLeadURL] = useState(duplicatedURL);
    const { user } = useAuth();

    useEffect(() => {
        setIsMarkedAsDuplicated(isDuplicated || Boolean(duplicatedURL));
        setOriginalLeadURL(duplicatedURL);
    }, [duplicatedURL, isDuplicated]);

    useEffect(() => {
        if (isMarkedAsDuplicated) return;

        let isActive = true;
        setIsLoadingMatch(true);

        getDuplicateLeadMatch(leadId)
            .then((match) => {
                if (isActive) setDuplicateMatch(match);
            })
            .catch(() => {
                if (isActive) {
                    setDuplicateMatch({
                        canMarkAsDuplicated: false,
                        reason: 'load-error',
                    });
                }
            })
            .finally(() => {
                if (isActive) setIsLoadingMatch(false);
            });

        return () => {
            isActive = false;
        };
    }, [isMarkedAsDuplicated, leadId]);

    const userHasPermissionToUpdate = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: DUPLICATED_LEAD_AUTH,
        crud: UPDATE_PERMISSION,
    });

    const handleSubmit = async () => {
        if (!userHasPermissionToUpdate || !duplicateMatch?.canMarkAsDuplicated)
            return;

        setIsSubmitting(true);
        try {
            const updatedLead = await setAsDuplicated(leadId);
            setOriginalLeadURL(
                updatedLead?.duplicatedURL || duplicateMatch.duplicatedURL
            );
            setIsMarkedAsDuplicated(true);
        } catch {
            // The lead context displays the API error.
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOpenLink = (url: string) =>
        window.open(url, '_blank', 'noopener,noreferrer');

    const getMatchMessage = () => {
        if (duplicateMatch?.canMarkAsDuplicated)
            return t('Leads.DuplicateMatchFound');

        if (duplicateMatch?.reason === 'original-lead')
            return t('Leads.OriginalLeadCannotBeDuplicated');

        if (duplicateMatch?.reason === 'already-duplicated')
            return t('Leads.LeadAlreadyDuplicated');

        if (duplicateMatch?.reason === 'load-error')
            return t('Leads.DuplicateMatchLoadError');

        return t('Leads.NoDuplicateMatchFound');
    };

    return (
        <Card {...props}>
            <CardHeader title={t('Leads.Duplicated')} />
            <Divider />
            <CardContent>
                {isLoadingMatch ? (
                    <div style={{ textAlign: 'center' }}>
                        <CircularProgress size={32} />
                    </div>
                ) : (
                    <Alert
                        severity={
                            isMarkedAsDuplicated
                                ? 'success'
                                : duplicateMatch?.canMarkAsDuplicated
                                ? 'info'
                                : 'warning'
                        }
                        style={{ marginBottom: '1em' }}
                    >
                        {isMarkedAsDuplicated
                            ? t('Leads.LeadAlreadyDuplicated')
                            : getMatchMessage()}
                    </Alert>
                )}
                {!isMarkedAsDuplicated && (
                    <Button
                        disabled={
                            !userHasPermissionToUpdate ||
                            !duplicateMatch?.canMarkAsDuplicated ||
                            isLoadingMatch ||
                            isSubmitting
                        }
                        style={{ height: 60, marginBottom: '1em' }}
                        variant="contained"
                        type="button"
                        size="large"
                        fullWidth
                        color="primary"
                        startIcon={<Edit />}
                        onClick={handleSubmit}
                    >
                        {t('Buttons.MarkAsDuplicated')}
                    </Button>
                )}
                {originalLeadURL && (
                    <Button
                        style={{ height: 60 }}
                        variant="contained"
                        type="button"
                        size="large"
                        fullWidth
                        color="primary"
                        startIcon={<ExternalLink />}
                        onClick={() => handleOpenLink(originalLeadURL)}
                    >
                        {t('Buttons.GoLead')}
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};
